import set from "lodash/set";
import store from '@/store';
import i18n from '@/language/i18n';
import activeWindowService from '../services/active-window-service';  

async function getWindow(type, id) {
  const response = await activeWindowService.getActiveWindow(type, id);
  if (response?.status !== 200) {
    return undefined;
  }
  return response?.data;
}

let openWindowMessageCooldown = false;
async function checkDirtyWindows() {
  const dirtyWindowsCount = (await activeWindowService.getAllActiveWindows()).flatMap(
    ({ type, windows }) => windows.map(({ id, data }) =>
      ({ type, id, isDirty: data?.isDirty }))
  ).filter(window => window.isDirty).length;

  if (dirtyWindowsCount > 25 && !openWindowMessageCooldown) {
    openWindowMessageCooldown = true;
    store.dispatch('globalSnackbarStore/showSnackbarWithMessage', {
      message: i18n.t('Components.sentences.performanceWarning'),
      type: 'warning',
      timeout: 5000,
      persistent: true
    });
  
  setTimeout(() => {
      openWindowMessageCooldown = false;
    }, 5000);
  }
}

async function pruneExcessCleanWindows() {
  const allCleanWindows = (await activeWindowService.getAllActiveWindows()).flatMap(({ type, windows }) => 
    windows
      .filter(window => window.data?.isDirty !== true)
      .map(window => ({ 
        type, 
        id: window.id, 
        lastEntryMillis: window.data?.lastEntryMillis || Date.now() 
      }))
  );
  
  const windowsMaxSize = 25;
  const sortedCleanWindows = allCleanWindows.sort((a, b) => (a.lastEntryMillis > b.lastEntryMillis) ? 1 : -1);
  
  if (sortedCleanWindows.length > windowsMaxSize) {
    const windowsToRemove = sortedCleanWindows.slice(0, sortedCleanWindows.length-windowsMaxSize);
    windowsToRemove.forEach(({ type, id }) => activeWindowService.deleteActiveWindow(type, id));
  }
}

export default {
  async addWindow(windowIdentifier, data) {
    let existingWindow = await getWindow(windowIdentifier.type, windowIdentifier.id);

    if(existingWindow !== undefined)
      return existingWindow;

    await activeWindowService.postActiveWindow(windowIdentifier.type, windowIdentifier.id, data);

    pruneExcessCleanWindows();
  },

  async addRoutingHistory(dto, preLoadedWindow) {
    const window = preLoadedWindow ?? await getWindow(dto.type, dto.id);
    if (window == undefined)  
      return; 
    
    set(window.data, 'routingHistory', dto.routingHistory);
    await activeWindowService.postActiveWindow(dto.type, dto.id, window.data);
  },

  async removeWindow(dto) {
    await activeWindowService.deleteActiveWindow(dto.type, dto.id);
  },

  async updateWindowCache({ type, id }, label, htmlLabel, isDirty, lastEntryMillis, persistentData, transientData) {
    const window = await getWindow(type, id);
    if (window === undefined) { return; }

    if(window.data == undefined || window.data === "")
      window.data = {};

    window.data['label'] = label;
    window.data['htmlLabel'] = htmlLabel;
    window.data['isDirty'] = isDirty;
    window.data['lastEntryMillis'] = lastEntryMillis;
    window.data['persistentData'] = persistentData;
    window.data['transientData'] = transientData;
    await activeWindowService.postActiveWindow(type, id, window.data);

    await checkDirtyWindows();
  },

  async setWindowDataField({ type, id, targetFieldPath, value }) { 
    const properties = targetFieldPath.split('.');
    const lastIndex = properties.length - 1;

    const window = await getWindow(type, id);
    if (window == undefined) { return; }

    let currentProp = window.data;

    for (let propIndex = 0; propIndex < lastIndex; propIndex++) {
      var propName = properties[propIndex];
      if (!currentProp.hasOwnProperty(propName) || currentProp[propName] == undefined) 
        set(currentProp, propName, {})
      currentProp = currentProp[propName];
    }

    var lastProperty = properties[lastIndex];
    if (!currentProp.hasOwnProperty(lastProperty) || Array.isArray(currentProp)) 
      set(currentProp, lastProperty, value);
    else 
      currentProp[lastProperty] = value;

    await activeWindowService.postActiveWindow(type, id, window.data);
  },

  async removeWindowAndDeleteTableReferences(dto) {
    const window = await getWindow(dto.type, dto.id);
    const oldRoutingHistory = window?.data?.routingHistory;

    if (oldRoutingHistory !== undefined) {
      const tableCaches = oldRoutingHistory.map(_item => {
        return { ..._item.to.query, ..._item.from.query }
      }).filter((_query) => _query['table-cache-id'] !== undefined)
        .map((_tableIdentifier) => _tableIdentifier['table-cache-id']);
      store.commit('tableStore/deleteTableReferences', tableCaches, { root: true });
    }

    await activeWindowService.deleteActiveWindow(dto.type, dto.id);
  },

  async removeWindowsAndDeleteTableReferences(dtos) {
    const data = {
      data: dtos.map((dto) => { return { id: dto.id, type: dto.type } })
    }

    const windows = await activeWindowService.getActiveWindows(data);

    const tableCaches = windows.flatMap( (window) => { 
      const oldRoutingHistory  = window?.data?.routingHistory

      if (oldRoutingHistory !== undefined)
        return oldRoutingHistory.map(_item => {
          return { ..._item.to.query, ..._item.from.query }
        }).filter((_query) => _query['table-cache-id'] !== undefined)
          .map((_tableIdentifier) => _tableIdentifier['table-cache-id'])
      
      return [];
    })
    store.commit('tableStore/deleteTableReferences', tableCaches, { root: true });

    await activeWindowService.deleteActiveWindows(data);
  },

  async getAllActiveWindows() {
    let windows = await activeWindowService.getAllActiveWindows();
    return windows.flatMap(
      ({ type, windows }) => windows.map(({ id, data }) => ({ 
        type, 
        id, 
        label: data?.label, 
        htmlLabel: data?.htmlLabel, 
        isDirty: data?.isDirty, 
        customLink: data?.customLink, 
        lastEntryMillis: data?.lastEntryMillis 
      })));
  },

  getWindowData: async ({ type, id }) => (await getWindow(type, id))?.data,

  getWindow: async ({ type, id }) => await getWindow(type, id),
  
}
